import React, { useState, useEffect, useRef } from 'react';
import GrantitudeSpiral from './GrantitudeSpiral';

/**
 * ⭐ MAIN LANDING PAGE - PRIMARY ENTRY POINT ⭐
 * SpiralOrg Entry Portal - Based on Secret History MultiPath Design
 * THIS IS THE MAIN EFFECT LANDING PAGE - REFERENCE THIS FOR CHANGES
 * 
 * Features:
 * - Full-screen overwhelming spiral.mp4 video
 * - ORIGINAL Grant spiral rotation effect (Secret History style)
 * - "By Invitation Only" entry point
 * - Leads to Cinema Experience → Ultimate Landing
 */
const SpiralOrgEntryPortal = ({ onProceedToCinema }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  // Grant spiral animation - ORIGINAL SECRET HISTORY EFFECT
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  useEffect(() => {
    // Auto-play video MUTED and slowed down
    if (videoRef.current) {
      const video = videoRef.current;
      video.playbackRate = 0.4; // 60% slower
      video.muted = true; // Keep muted
      
      // Play video
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(e => console.log('Video autoplay blocked:', e));
      }
      
      // Smooth loop transition
      const handleTimeUpdate = () => {
        if (!video.duration) return; // Safety check
        
        const duration = video.duration;
        const currentTime = video.currentTime;
        
        // Fade out in last 0.8 seconds
        if (duration - currentTime < 0.8) {
          video.style.opacity = (duration - currentTime) / 0.8;
        }
        // Fade in during first 0.8 seconds
        else if (currentTime < 0.8) {
          video.style.opacity = currentTime / 0.8;
        }
        // Full opacity in middle
        else {
          video.style.opacity = 1;
        }
      };
      
      video.addEventListener('timeupdate', handleTimeUpdate);
      
      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, []);
  
  useEffect(() => {
    // Auto-play background audio on user interaction
    const playAudio = () => {
      if (audioRef.current) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(e => console.log('Audio autoplay blocked:', e));
        }
      }
    };
    
    document.addEventListener('click', playAudio, { once: true });
    document.addEventListener('touchstart', playAudio, { once: true });
  }, []);

  return (
    <div 
      className="min-h-screen relative overflow-hidden bg-black"
    >
      {/* Background Audio Loop - Separate from video */}
      <audio ref={audioRef} loop preload="auto">
        <source src="https://customer-assets.emergentagent.com/job_spiralorg/artifacts/0vzjt9th_download.mp4" type="audio/mp4" />
      </audio>

      {/* FULL-SCREEN OVERWHELMING Video Background - Family and Tree Animation */}
      <div className="fixed inset-0 z-0">
        <video 
          ref={videoRef}
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto object-cover"
          style={{ 
            transform: 'translate(-50%, -50%) scale(1.15)',
            filter: 'brightness(0.5) saturate(0.9)'
          }}
        >
          <source src="https://customer-assets.emergentagent.com/job_spiralorg/artifacts/bvnlxxwi_Animating_Family_and_Tree_in_Video.mov" type="video/mp4" />
        </video>
      </div>
      
      {/* Spiral Tree Video - Semi-transparent overlay for Grant math visibility */}
      <div className="fixed inset-0 z-[1]" style={{ opacity: 0.4 }}>
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto object-cover"
          style={{ 
            transform: 'translate(-50%, -50%) scale(1.3)',
            filter: 'brightness(1.1) contrast(1.2)',
            mixBlendMode: 'screen'
          }}
        >
          <source src="/spiral-tree-animation.mov" type="video/mp4" />
        </video>
      </div>

      {/* ORIGINAL Grant spiral effect from Secret History - rotating behind */}
      <div className="fixed inset-0 pointer-events-none opacity-30" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(8) rotate(${spiralPhase}deg)`,
            filter: 'blur(3px) brightness(1.6) saturate(1.8)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={300} />
        </div>
      </div>

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-transparent to-black/60 z-[2]"></div>

      <div className="relative z-10 flex flex-col min-h-screen">
        
        {/* Top Section - SpiralOrg Title and Subtitle */}
        <div className="flex-1 flex items-center justify-center px-4 pt-20">
          <button
            onClick={onProceedToCinema}
            className="group transition-all duration-300 hover:scale-105 cursor-pointer px-4"
          >
            <h1 
              className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold leading-none tracking-wider"
              style={{
                fontFamily: "'Cinzel', serif",
                textShadow: '0 0 50px rgba(230, 198, 133, 1.2), 0 0 100px rgba(230, 198, 133, 0.9)',
                letterSpacing: '0.35em',
                color: '#E6C794',
                filter: 'brightness(1.3)',
                wordBreak: 'keep-all',
                overflowWrap: 'normal'
              }}
            >
              <span style={{ display: 'inline-block' }}>
                <span style={{ textDecoration: 'underline', textDecorationColor: 'rgba(230, 198, 133, 0.5)', textUnderlineOffset: '0.15em' }}>
                  <span style={{ fontSize: '1.55em' }}>S</span>piral<span style={{ fontSize: '1.35em' }}>O</span>rg
                </span>
                <span 
                  style={{ 
                    display: 'block',
                    fontSize: '0.3em', 
                    letterSpacing: '0.2em',
                    textAlign: 'right',
                    marginTop: '-0.3em',
                    paddingRight: '0.5em'
                  }}
                >
                  .org
                </span>
              </span>
            </h1>
            
            {/* Three-Line Subtitle */}
            <div className="mt-8 space-y-2">
              <p 
                className="text-lg md:text-xl lg:text-2xl font-light tracking-widest"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  textShadow: '0 0 25px rgba(230, 198, 133, 0.8)',
                  letterSpacing: '0.2em',
                  color: '#E6C794'
                }}
              >
                A Private Guild,
              </p>
              <p 
                className="text-lg md:text-xl lg:text-2xl font-light tracking-widest"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  textShadow: '0 0 25px rgba(230, 198, 133, 0.8)',
                  letterSpacing: '0.2em',
                  color: '#E6C794'
                }}
              >
                Activating Family Mastery,
              </p>
              <p 
                className="text-lg md:text-xl lg:text-2xl font-light tracking-widest"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  textShadow: '0 0 25px rgba(230, 198, 133, 0.8)',
                  letterSpacing: '0.2em',
                  color: '#E6C794'
                }}
              >
                Catalyzed by Family Re-Union
              </p>
            </div>
          </button>
        </div>

        {/* Bottom Section - Chapter 184 Question + Invitation Box */}
        <div className="pb-16 px-4">
          {/* Chapter 184 - Lower on page (1/3 from bottom) */}
          <div className="max-w-2xl mx-auto space-y-4 mb-8">
            <p 
              className="text-base md:text-lg font-light text-amber-200/70 italic text-center"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                letterSpacing: '0.05em',
                textShadow: '0 0 15px rgba(230, 198, 133, 0.4)'
              }}
            >
              What is required to form a private guild?
            </p>
            
            <p 
              className="text-xl md:text-2xl text-white/90 leading-relaxed text-center"
              style={{
                fontFamily: "'Cinzel', serif",
                letterSpacing: '0.08em',
                textShadow: '0 0 20px rgba(230, 198, 133, 0.6)'
              }}
            >
              "Three or more joined by mutual consent<br/>
              for a common purpose."
            </p>
            
            <p 
              className="text-xs text-amber-300/50 italic text-center"
              style={{
                fontFamily: "'Courier New', monospace",
                letterSpacing: '0.1em'
              }}
            >
              — Wisconsin Chapter 184
            </p>
          </div>

          {/* Tiny "By Invitation Only" Box - 50% smaller */}
          <div className="flex justify-center">
            <div 
              onClick={onProceedToCinema}
              className="cursor-pointer group bg-black/70 backdrop-blur-md border border-amber-500/40 hover:border-amber-500/70 rounded-xl px-4 py-2 transition-all duration-300 hover:scale-110 max-w-[200px]"
            >
              <p 
                className="text-xs font-bold tracking-widest text-amber-400/90 group-hover:text-amber-300"
                style={{
                  fontFamily: "'Cinzel', serif",
                  letterSpacing: '0.15em'
                }}
              >
                BY INVITATION ONLY
              </p>
              <p 
                className="text-[10px] text-amber-300/60 group-hover:text-amber-200/80 mt-0.5 transition-colors text-center"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Click to Proceed →
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiralOrgEntryPortal;
